import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Col, Form, Row, Spinner } from '@/components/ui';
import ResponsiveModal from '../../../components/ResponsiveModal';
import type { PcrSubmission } from '../../../services/pcrService';
import {
  fetchPcrReplyTemplates,
  postPcrSubmissionReply,
  type PcrReplyTemplate,
} from '../../../services/pcrService';
import {
  applyPcrReplyPlaceholdersClient,
  defaultPcrReplySubject,
} from '../utils/pcrReplyPlaceholders';

export interface PcrReplyModalProps {
  isOpen: boolean;
  submission: PcrSubmission | null;
  onClose: () => void;
  /** Called after a successful send so the parent can refresh list data. */
  onSent: () => void;
  /** Opens Notes & history on the Contact log tab for this PCR. */
  onViewContactLog?: () => void;
}

const PcrReplyModal: React.FC<PcrReplyModalProps> = ({
  isOpen,
  submission,
  onClose,
  onSent,
  onViewContactLog,
}) => {
  const [templates, setTemplates] = useState<PcrReplyTemplate[]>([]);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  const [subject, setSubject] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [templateId, setTemplateId] = useState<number | ''>('');
  const [sendError, setSendError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const row = submission;

  const resetForm = useCallback(() => {
    setSubject('');
    setBodyText('');
    setTemplateId('');
    setSendError(null);
  }, []);

  useEffect(() => {
    if (!isOpen || !row) return;
    resetForm();
    setSubject(defaultPcrReplySubject(row));
    setTemplatesError(null);
    setTemplatesLoading(true);
    void fetchPcrReplyTemplates()
      .then((t) => setTemplates(t))
      .catch((e: Error) => setTemplatesError(e.message || 'Failed to load templates'))
      .finally(() => setTemplatesLoading(false));
  }, [isOpen, row, resetForm]);

  const htmlToPlainText = (html: string): string => {
    if (typeof document === 'undefined') {
      return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    }
    const d = document.createElement('div');
    d.innerHTML = html;
    return (d.textContent || '').replace(/\r\n/g, '\n').trim();
  };

  const onSelectTemplate = (idStr: string) => {
    if (!row) return;
    if (!idStr) {
      setTemplateId('');
      setSubject(defaultPcrReplySubject(row));
      setBodyText('');
      return;
    }
    const id = parseInt(idStr, 10);
    const tpl = templates.find((t) => t.id === id);
    if (!tpl) return;
    setTemplateId(id);
    setSubject(applyPcrReplyPlaceholdersClient(tpl.subjectPattern, row));
    setBodyText(htmlToPlainText(applyPcrReplyPlaceholdersClient(tpl.bodyHtml, row)));
  };

  const recipient = useMemo(() => (row?.user_email || '').trim(), [row]);

  const handleSend = async () => {
    if (!row) return;
    setSendError(null);
    setSending(true);
    try {
      await postPcrSubmissionReply(row.id, {
        subject: subject.trim(),
        bodyText: bodyText.trim(),
        templateId: templateId === '' ? null : templateId,
      });
      onSent();
      onClose();
    } catch (e) {
      setSendError(e instanceof Error ? e.message : 'Send failed');
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      title="Reply to submitter"
      size="medium"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={sending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void handleSend()} disabled={sending || !recipient}>
            {sending ? (
              <>
                <Spinner animation="border" size="sm" className="tw-mr-2" />
                Sending…
              </>
            ) : (
              'Send email'
            )}
          </Button>
        </>
      }
    >
      {!row ? (
        <p className="tw-text-content-muted tw-mb-0">No submission selected.</p>
      ) : (
        <>
          {templatesError && (
            <Alert variant="warning" className="tw-text-sm">
              {templatesError}
            </Alert>
          )}
          {sendError && (
            <Alert variant="danger" className="tw-text-sm">
              {sendError}
            </Alert>
          )}
          <Row className="tw-mb-3">
            <Col md={6}>
              <Form.Label className="tw-text-sm tw-text-content-muted tw-mb-0">To</Form.Label>
              <Form.Control size="sm" readOnly value={recipient || '–'} className={recipient ? '' : 'tw-text-danger'} />
              {!recipient && <div className="tw-text-sm tw-text-danger tw-mt-1">This row has no email address.</div>}
            </Col>
            <Col md={6}>
              <Form.Label className="tw-text-sm tw-text-content-muted tw-mb-0">Template (optional)</Form.Label>
              <Form.Select
                size="sm"
                value={templateId === '' ? '' : String(templateId)}
                onChange={(e) => onSelectTemplate(e.target.value)}
                disabled={templatesLoading || templates.length === 0}
              >
                <option value="">– None –</option>
                {templates.map((t) => (
                  <option key={t.id} value={String(t.id)}>
                    {t.name}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={12}>
              <Form.Label className="tw-text-sm tw-text-content-muted tw-mb-0">Subject</Form.Label>
              <Form.Control
                size="sm"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={500}
              />
            </Col>
            <Col xs={12}>
              <Form.Label className="tw-text-sm tw-text-content-muted tw-mb-0">Message</Form.Label>
              <Form.Control
                as="textarea"
                rows={10}
                value={bodyText}
                onChange={(e) => setBodyText(e.target.value)}
                placeholder="Compose your reply…"
              />
            </Col>
          </Row>

          {onViewContactLog ? (
            <p className="tw-text-sm tw-text-content-muted tw-mb-0">
              Past sends are recorded in{' '}
              <Button variant="link" size="sm" className="tw-p-0 tw-align-baseline" onClick={onViewContactLog}>
                Notes &amp; history → Contact log
              </Button>
              .
            </p>
          ) : null}
        </>
      )}
    </ResponsiveModal>
  );
};

export default PcrReplyModal;
